import { isMacPlatform, migrateLegacyModifier } from 'src/util/shortcuts/keyNormalizer';

interface RawBinding {
	enabled: boolean;
	keys: string;
}

type RawBindingMap = Record<string, RawBinding>;

/**
 * Abbildung historischer per-Typ-Action-IDs auf die generischen Detail-Actions.
 * Reihenfolge = Präzedenz: die erste Quelle mit nicht-leeren Keys gewinnt.
 */
const LEGACY_ACTION_ID_MAP: ReadonlyArray<{ legacyIds: ReadonlyArray<string>; targetId: string }> = [
	{ legacyIds: ['task.delete', 'note.delete'], targetId: 'detail.delete' },
	{ legacyIds: ['task.save', 'note.save'], targetId: 'detail.save' },
];

function isWellFormed(binding: RawBinding | undefined): binding is RawBinding {
	return binding !== undefined && typeof binding.keys === 'string' && typeof binding.enabled === 'boolean';
}

/**
 * Migriert einen rohen Binding-Bestand (localStorage oder Server-Preference):
 * legacy per-Typ-IDs werden auf `detail.*` umgeschrieben und alle Keys auf das
 * plattformneutrale `Mod`-Token gehoben. Ein bereits vorhandenes modernes Binding
 * hat Vorrang vor jeder Legacy-Quelle.
 */
export function migrateBindingIds(raw: RawBindingMap, isMac: boolean = isMacPlatform()): RawBindingMap {
	const migrated: RawBindingMap = {};
	const legacyIdLookup = new Set(LEGACY_ACTION_ID_MAP.flatMap((entry) => entry.legacyIds));

	for (const [actionId, binding] of Object.entries(raw)) {
		if (legacyIdLookup.has(actionId) || !isWellFormed(binding)) {
			continue;
		}

		migrated[actionId] = {
			enabled: binding.enabled,
			keys: migrateLegacyModifier(binding.keys, isMac),
		};
	}

	for (const { legacyIds, targetId } of LEGACY_ACTION_ID_MAP) {
		if (targetId in migrated) {
			continue;
		}

		const source = legacyIds
			.map((legacyId) => raw[legacyId])
			.find((entry) => isWellFormed(entry) && entry.keys.length > 0);

		if (source === undefined) {
			continue;
		}

		migrated[targetId] = {
			enabled: source.enabled,
			keys: migrateLegacyModifier(source.keys, isMac),
		};
	}

	return migrated;
}
